/**
 * Auto-reschedule matches:
 * 1) Detect conflicts: two or more matches scheduled for the same slot (same date + time).
 * 2) Collect all matches to reschedule (overdue + future with a slot), sort chronologically.
 * 3) Assign each match in order to the next available slot so that no two matches share a slot
 *    and order is preserved (earliest match → earliest free slot, etc.).
 * Runs client-side when admin clicks the button.
 */

import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  addDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { formatDate } from '../utils/dateFormat';
import { ensureVenueBlockForMatch } from './venueBlockService';
import {
  generateRescheduleSlots,
  generateRescheduleSlotsStrict,
  isMatchOverdue,
  parseTimeToMins,
} from '../utils/scheduleStrategy';
import { getAvailabilityForPlayers, createAvailabilityChecker } from './playerAvailabilityService';
import { toUtcMs } from '../utils/dateTimeUtils';

function getParticipantIds(match) {
  if (match.matchType === 'doubles') {
    return [...(match.team1Players || []), ...(match.team2Players || [])];
  }
  return [match.player1Id, match.player2Id].filter(Boolean);
}

const LOG_TYPES = { RESCHEDULE_COMPLETED: 'reschedule_completed' };

function toPlainDate(dateOrTimestamp) {
  const ms = toUtcMs(dateOrTimestamp);
  return ms != null ? new Date(ms) : null;
}

/** Build slot key YYYY-MM-DD|HH:MM for conflict detection and taken set. */
function getSlotKey(dateOrTimestamp, timeStr) {
  const d = toPlainDate(dateOrTimestamp);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}|${(timeStr || '').trim()}`;
}

/** Sort matches by scheduled date/time ascending (earliest first). */
function sortByScheduledAsc(matches) {
  return [...matches].sort((a, b) => {
    const dA = toPlainDate(a.scheduledDate);
    const dB = toPlainDate(b.scheduledDate);
    if (!dA || !dB) return 0;
    const dayA = new Date(dA.getFullYear(), dA.getMonth(), dA.getDate()).getTime();
    const dayB = new Date(dB.getFullYear(), dB.getMonth(), dB.getDate()).getTime();
    if (dayA !== dayB) return dayA - dayB;
    return parseTimeToMins(a.scheduledTime || '') - parseTimeToMins(b.scheduledTime || '');
  });
}

/**
 * Find slots that have more than one match (conflicts).
 * @returns {Map<string, Array<{ id, scheduledDate, scheduledTime }>>} slotKey -> matches in that slot
 */
function findSlotConflicts(matches) {
  const bySlot = new Map();
  for (const m of matches) {
    if (!m.scheduledDate) continue;
    const key = getSlotKey(m.scheduledDate, m.scheduledTime);
    if (!key) continue;
    if (!bySlot.has(key)) bySlot.set(key, []);
    bySlot.get(key).push(m);
  }
  const conflicts = new Map();
  for (const [key, list] of bySlot) {
    if (list.length > 1) conflicts.set(key, list);
  }
  return conflicts;
}

/**
 * Check if a tournament has required venue and time slots for reschedule.
 * Requires at least one venue (defaultVenueId or venueIds with length) and scheduleConfig with weekdays, scheduleStart, scheduleEnd.
 */
function tournamentHasVenueAndSlots(tournament) {
  if (!tournament) return false;
  const hasVenue = !!(tournament.defaultVenueId || (Array.isArray(tournament.venueIds) && tournament.venueIds.length > 0));
  const config = tournament.scheduleConfig || {};
  const hasSlots = !!(config.scheduleStart != null && config.scheduleEnd != null && Array.isArray(config.weekdays) && config.weekdays.length > 0);
  return hasVenue && hasSlots;
}

/**
 * Run auto-reschedule:
 * - Requires each tournament to have venue(s) and time slots; skips tournaments without them.
 * - Option useBuffer: true = extended window (1hr earlier, 30min later); false = actual schedule only.
 * - Detect same-slot conflicts and reassign all affected matches in chronological order; no two matches share a slot.
 * @param {{ useBuffer?: boolean, gameId?: string }} [options] - useBuffer: use extended time window (default true); gameId: only reschedule matches for this game
 * @returns {{ rescheduled: number, errors: string[], conflictsResolved?: number, skippedTournaments?: string[] }}
 */
export async function runAutoReschedule(options = {}) {
  const useBuffer = options.useBuffer !== false;
  const gameIdFilter = options.gameId || null;
  const today = new Date();
  const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const errors = [];
  let rescheduled = 0;
  let conflictsResolved = 0;

  const matchesSnap = await getDocs(
    query(collection(db, 'matches'), where('status', 'in', ['scheduled', 'in-progress']))
  );

  let allMatches = matchesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  if (gameIdFilter) {
    allMatches = allMatches.filter((m) => (m.gameId || 'table-tennis') === gameIdFilter);
  }
  const overdue = allMatches.filter((m) => m.scheduledDate && isMatchOverdue(m, today));
  const future = allMatches.filter((m) => {
    if (!m.scheduledDate || m.status === 'completed') return false;
    const sched = toPlainDate(m.scheduledDate);
    const schedDay = new Date(sched.getFullYear(), sched.getMonth(), sched.getDate());
    return schedDay >= startOfToday;
  });

  const byTournament = new Map();
  for (const m of overdue) {
    const tid = m.tournamentId || '_none';
    if (!byTournament.has(tid)) byTournament.set(tid, { overdue: [], future: [] });
    byTournament.get(tid).overdue.push(m);
  }
  for (const m of future) {
    const tid = m.tournamentId || '_none';
    if (!byTournament.has(tid)) byTournament.set(tid, { overdue: [], future: [] });
    byTournament.get(tid).future.push(m);
  }

  const skippedTournaments = [];

  for (const [tournamentId, { overdue: overdueMatches, future: futureMatches }] of byTournament) {
    const toReschedule = sortByScheduledAsc([...overdueMatches, ...futureMatches]);
    if (toReschedule.length === 0) continue;

    let tournament = null;
    let config = null;
    if (tournamentId !== '_none') {
      const tSnap = await getDoc(doc(db, 'tournaments', tournamentId));
      if (tSnap.exists()) {
        tournament = { id: tSnap.id, ...tSnap.data() };
        config = tournament.scheduleConfig || null;
      }
    }
    if (!tournamentHasVenueAndSlots(tournament)) {
      const name = tournament?.name || tournamentId;
      skippedTournaments.push(name);
      errors.push(`Tournament "${name}" (${tournamentId}): missing venue or time slots. Add at least one venue and set schedule (weekdays, start/end time) before reschedule.`);
      continue;
    }
    if (!config) {
      config = { scheduleStart: '18:00', scheduleEnd: '21:00', matchDuration: 30, weekdays: [1, 3, 5] };
    }

    let availabilityChecker = null;
    if (tournament?.askPlayerAvailability && tournamentId !== '_none') {
      const allPlayerIds = new Set();
      toReschedule.forEach((m) => getParticipantIds(m).forEach((id) => allPlayerIds.add(id)));
      const availabilityByPlayer = await getAvailabilityForPlayers(tournamentId, [...allPlayerIds]);
      availabilityChecker = createAvailabilityChecker(availabilityByPlayer);
    }

    const conflicts = findSlotConflicts(toReschedule);
    for (const [, list] of conflicts) conflictsResolved += list.length;

    const otherMatches = allMatches.filter(
      (m) => m.tournamentId === tournamentId && !toReschedule.find((r) => r.id === m.id)
    );
    const taken = new Set();
    for (const m of otherMatches) {
      if (m.scheduledDate) {
        const key = getSlotKey(m.scheduledDate, m.scheduledTime);
        if (key) taken.add(key);
      }
    }

    const minSlots = toReschedule.length + 20;
    const allSlots = useBuffer
      ? generateRescheduleSlots(config, minSlots)
      : generateRescheduleSlotsStrict(config, minSlots);

    for (const match of toReschedule) {
      try {
        const participantIds = getParticipantIds(match);
        let slot = null;
        for (const s of allSlots) {
          const key = getSlotKey(s.date, s.time);
          if (!taken.has(key)) {
            if (typeof availabilityChecker === 'function' && !availabilityChecker(s, participantIds)) continue;
            slot = { date: new Date(s.date), time: s.time };
            taken.add(key);
            break;
          }
        }
        if (!slot) {
          errors.push(`No slot for match ${match.id} (tournament ${tournamentId})`);
          continue;
        }

        const oldDateObj = toPlainDate(match.scheduledDate);
        const oldTime = match.scheduledTime || '';
        const durationMins = config.matchDuration || 30;
        const updatedMatch = { ...match, scheduledDate: slot.date, scheduledTime: slot.time };
        const venueBlockId = match.venueId
          ? await ensureVenueBlockForMatch(updatedMatch, durationMins)
          : null;
        const updatePayload = {
          scheduledDate: slot.date,
          scheduledTime: slot.time,
          rescheduleRequested: null,
          rescheduleRequestedBy: null,
          rescheduleRequestedAt: null,
          rescheduleApprovedBy: null,
          rescheduleRequestedDate: null,
          rescheduleRequestedTime: null,
          updatedAt: Timestamp.now(),
        };
        if (venueBlockId != null) updatePayload.venueBlockId = venueBlockId;
        await updateDoc(doc(db, 'matches', match.id), updatePayload);
        await addDoc(collection(db, 'matchUpdateLogs'), {
          matchId: match.id,
          type: LOG_TYPES.RESCHEDULE_COMPLETED,
          oldDate: oldDateObj ? formatDate(oldDateObj) : '',
          oldTime,
          newDate: formatDate(slot.date),
          newTime: slot.time,
          performedBy: null,
          performedByName: 'Auto-reschedule (chronological, no double-book)',
          performedAt: Timestamp.now(),
        });
        rescheduled++;
      } catch (err) {
        console.error('[autoReschedule] match', match.id, err);
        errors.push(`Match ${match.id}: ${err.message}`);
      }
    }
  }

  return { rescheduled, errors, conflictsResolved, skippedTournaments };
}
